import { toMoney, toPercent } from '@utils/Format'
import {
  LIQUIDEZ_BUCKETS,
  LIQUIDEZ_SEM_DADO_LABEL,
} from '@modules/consolidator/constants/Liquidez'

export const formatLiquidezChart = (walletAnalysis, { currency = 'real' } = {}) => {
  const fromBackend = readBackendBreakdown(walletAnalysis, currency)
  if (fromBackend) return fromBackend
  return computeFromAtivos(walletAnalysis, currency)
}

const readBackendBreakdown = (walletAnalysis, currency) => {
  if (!walletAnalysis) return null
  const breakdown = walletAnalysis.liquidez
  if (!Array.isArray(breakdown) || breakdown.length === 0) return null
  return breakdown
    .map((entry) => ({
      name: entry && entry.name,
      weight: entry && normalizeWeight(entry.weight),
      pl: entry && toMoney(entry.pl, 2, currency),
    }))
    .filter((entry) => entry.name && entry.weight !== null)
}

const normalizeWeight = (weight) => {
  if (typeof weight === 'number' && Number.isFinite(weight)) {
    return toPercent(weight * 100)
  }
  if (typeof weight === 'string' && weight.length > 0) return weight
  return null
}

const computeFromAtivos = (walletAnalysis, currency) => {
  const grupos = readGrupos(walletAnalysis)
  if (grupos.length === 0) return []
  const totals = aggregateByBucket(grupos)
  const totalPl = sumValues(totals)
  if (totalPl <= 0) return []
  return buildEntriesInBucketOrder(totals, totalPl, currency)
}

const readGrupos = (walletAnalysis) => {
  if (!walletAnalysis || !walletAnalysis.posicao_completa) return []
  const grupos = walletAnalysis.posicao_completa.grupos
  return Array.isArray(grupos) ? grupos : []
}

const aggregateByBucket = (grupos) => {
  const totals = {}
  for (const grupo of grupos) {
    addGrupoToTotals(totals, grupo)
  }
  return totals
}

const addGrupoToTotals = (totals, grupo) => {
  if (!grupo) return
  const ativos = Array.isArray(grupo.ativos) ? grupo.ativos : []
  for (const ativo of ativos) {
    if (!ativo) continue
    if (typeof ativo.pl !== 'number' || !Number.isFinite(ativo.pl)) continue
    const label = resolveBucketLabel(ativo.liquidez)
    totals[label] = (totals[label] || 0) + ativo.pl
  }
}

const resolveBucketLabel = (liquidez) => {
  if (typeof liquidez !== 'number' || !Number.isFinite(liquidez)) {
    return LIQUIDEZ_SEM_DADO_LABEL
  }
  const bucket = LIQUIDEZ_BUCKETS.find(
    ({ min, max }) => liquidez >= min && liquidez <= max
  )
  return bucket ? bucket.label : LIQUIDEZ_SEM_DADO_LABEL
}

const sumValues = (totals) =>
  Object.values(totals).reduce((acc, value) => acc + value, 0)

const buildEntriesInBucketOrder = (totals, totalPl, currency) => {
  const entries = []
  for (const { label } of LIQUIDEZ_BUCKETS) {
    appendEntry(entries, totals, label, totalPl, currency)
  }
  appendEntry(entries, totals, LIQUIDEZ_SEM_DADO_LABEL, totalPl, currency)
  return entries
}

const appendEntry = (entries, totals, label, totalPl, currency) => {
  const pl = totals[label]
  if (typeof pl !== 'number') return
  entries.push({
    name: label,
    pl: toMoney(pl, 2, currency),
    weight: toPercent((pl / totalPl) * 100),
  })
}
